import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CContainer,
  CSpinner,
  CAlert,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
} from "@coreui/react";
import { CChartPie } from "@coreui/react-chartjs";
import settingsStore from "../../store/SettingsStore";
import datasStore from "../../store/DataStore";
import DashboardFilters from "../UI/PeriodFilters/DashboardFilters";
import { buildPieItems, fmtInt, sumNums } from "../../utils/chartHelpers";
import { formatYmd } from "../../utils/dateRange";
import { logReportView } from "../../api/reportLogApi";

const categoryPalette = ["#FF6384", "#36A2EB", "#FFCE56", "#ff2e12ff", "#23e033ff", "#32aebeff", "#d15bd1ff"];

const CategoriesPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState("");
  const [quickRange, setQuickRange] = useState("week");

  const load = useCallback(
    (from, to) => {
      if (!selectedServer || !from || !to) return;
      datasStore.loadPeriodData(selectedServer, from, to);
    },
    [selectedServer],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      const today = new Date();
      const past = new Date(today);
      past.setDate(today.getDate() - 7);
      datasStore.setFromDate(formatYmd(past));
      datasStore.setToDate(formatYmd(today));
      logReportView({
        event: "report_view",
        reportKey: "categories",
        reportTitle: "Категории блюд",
        userId,
        path: "/categories",
      });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (!selectedServer || !datasStore.fromDate || !datasStore.toDate) return;
    load(datasStore.fromDate, datasStore.toDate);
  }, [selectedServer, load]);

  const items = buildPieItems(datasStore.categoryName, datasStore.categorySale, categoryPalette);
  const total = sumNums(items.map((x) => x.value));

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Продажи по категориям</h4>
      <DashboardFilters
        servers={settingsStore.servers}
        selectedServer={selectedServer}
        onServerChange={(e) => setSelectedServer(e.target.value)}
        fromDate={datasStore.fromDate}
        toDate={datasStore.toDate}
        onFromChange={(d) => datasStore.setFromDate(d)}
        onToChange={(d) => datasStore.setToDate(d)}
        onRefresh={load}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={datasStore.loading}
      />
      {datasStore.error && (
        <CAlert color="danger" className="mt-3">
          {datasStore.error}
        </CAlert>
      )}
      {datasStore.loading && (
        <div className="text-center py-4">
          <CSpinner />
        </div>
      )}
      {!datasStore.loading && (
        <CCard className="mt-4">
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <span>Категории за период</span>
            <Link to="/dashboard" className="btn btn-sm btn-outline-primary">
              На дашборд
            </Link>
          </CCardHeader>
          <CCardBody>
            <CChartPie
              data={{
                labels: items.map((x) => x.label),
                datasets: [{ data: items.map((x) => x.value), backgroundColor: items.map((x) => x.color) }],
              }}
            />
            <CTable small hover responsive className="mt-4">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Категория</CTableHeaderCell>
                  <CTableHeaderCell>Сумма</CTableHeaderCell>
                  <CTableHeaderCell>Доля</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {items.map((it) => (
                  <CTableRow key={it.label}>
                    <CTableDataCell>{it.label}</CTableDataCell>
                    <CTableDataCell>{fmtInt(it.value)} ₽</CTableDataCell>
                    <CTableDataCell>
                      {total > 0 ? ((it.value / total) * 100).toFixed(1) : 0}%
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
            <p className="fw-semibold mb-0">Итого: {fmtInt(total)} ₽</p>
          </CCardBody>
        </CCard>
      )}
    </CContainer>
  );
});

export default CategoriesPage;
